import {
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  VisuallyHidden,
  VStack,
  useClipboard,
} from '@chakra-ui/react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TRIAL_CARDLESS_SEAT_CAP } from '@bower/shared/billing/policy';
import { getRoleLabel } from '../settings/workspaceUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useWorkspaceRole } from '../../hooks/user/useWorkspaceRole';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { apiService } from '../../services/api.service';
import {
  inviteGuest,
  inviteMemberDirect,
  previewAddSeat,
  purchaseSeatAndInvite,
  redeemPromotion,
} from '../../services/api/billing.api';
import { ContainerStack } from '../ui/ContainerStack';
import { MaterialIcon } from '../ui/MaterialIcon';
import { StatusBanner } from '../ui/StatusBanner';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useCreatePortalSession, useWorkspacePlan } from '../../hooks/data/useBilling';
import { useUpgradeGate } from '../../hooks/billing/useUpgradeGate';
import { queryKeys } from '../../lib/queryClient';
import { SeatConfirmationStage, formatUsd, seatPaymentMethodReason } from './SeatConfirmationStage';
import type { SeatPreviewView } from './SeatConfirmationStage';

interface InviteTeamMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type InviteMode = 'guest' | 'paid';
type Stage = 'compose' | 'confirm-seat' | 'success';

function buildIntroMessage(workspaceName: string, link: string): string {
  return `Hi! I'd like you to join ${workspaceName} on Bower: it's where we keep our lab notes, protocols, and AI assistant context together. Use this link to set up your account:\n\n${link}\n\nThe link is yours alone, so please don't forward it.`;
}

/**
 * `select()` leaves the caret at the end of the value, which scrolls the field
 * to its tail: a long invitation URL then shows only its trailing token
 * fragment, and a wrapped intro message shows only its last line. Both fields
 * exist to be recognised before they are sent, so rewind after selecting.
 *
 * Covers both share fields deliberately. The other `select()` call sites in
 * the app (InlineEdit, AttachmentsPanel) are
 * EDITABLE rename inputs where the user types immediately and the scroll
 * position is transient, so they are a different case and are left alone.
 */
function selectFromStart(field: HTMLInputElement | HTMLTextAreaElement) {
  field.select();
  field.scrollLeft = 0;
  field.scrollTop = 0;
}

/**
 * Workspace invite flow.
 *
 * Three stages:
 *   - compose: pick role + enter recipient email (a label, not a delivery
 *     channel). Submit creates the invitation.
 *   - confirm-seat: Stripe pro-rated charge preview before purchasing a
 *     paid seat (Team tier only).
 *   - success: link + share helpers. THIS IS THE DELIVERABLE. Bower
 *     does not email the invite, the admin does, so the link is the
 *     one loud element on the screen: monospace, full-width, primary
 *     copy button beside it. Everything else is deliberately quiet.
 *     The recipient/role confirmation is a single line, the guidance
 *     is one sentence, and the ready-to-paste intro message is a
 *     compact button with the full text behind a Preview toggle
 *     rather than an always-open textarea competing with the link.
 *
 * The modal does not auto-close on success — the admin has to dismiss it
 * deliberately, acknowledging they grabbed the link. Dismissal is the
 * top-right close X (project modal convention); the Members tab's Pending
 * invitations list keeps the link discoverable afterwards in case they
 * close without copying.
 */
export default function InviteTeamMemberDialog({ isOpen, onClose }: InviteTeamMemberDialogProps) {
  const { workspace } = useWorkspace();
  const workspaceRole = useWorkspaceRole();
  // Only an owner or admin may grant admin; the backend refuses anyone else.
  const canGrantAdmin = workspaceRole === 'OWNER' || workspaceRole === 'ADMIN';
  const toast = useAppToast();
  const queryClient = useQueryClient();
  const { data: plan } = useWorkspacePlan();
  const portalMutation = useCreatePortalSession();

  const tier = (plan?.tier ?? 'FREE') as 'FREE' | 'PRO' | 'TEAM';
  const isTeam = tier === 'TEAM';

  const [mode, setMode] = useState<InviteMode>('guest');
  const [email, setEmail] = useState('');
  const [paidRole, setPaidRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [promotionCode, setPromotionCode] = useState('');
  const [redeemingPromotion, setRedeemingPromotion] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<Stage>('compose');
  const [preview, setPreview] = useState<SeatPreviewView | null>(null);
  // The ready-to-paste intro is opt-in. Rendered open it is the tallest
  // thing on the success screen and it repeats the link verbatim, which
  // is what made that screen read as two competing offers.
  const [showMessage, setShowMessage] = useState(false);
  // Instance-scoped so two dialogs in one document cannot collide on the IDREF.
  const introMessageId = useId();

  // Success-state payload. Held in component state so the modal can
  // stay open after creation and the admin can grab the link.
  const [createdInvite, setCreatedInvite] = useState<{
    email: string;
    link: string;
    role: 'guest' | 'admin' | 'member';
  } | null>(null);

  const linkInputRef = useRef<HTMLInputElement | null>(null);
  // Built once so the clipboard payload and the preview can never drift.
  const introMessage = useMemo(
    () =>
      createdInvite
        ? buildIntroMessage(workspace?.name ?? 'our workspace', createdInvite.link)
        : '',
    [createdInvite, workspace?.name],
  );
  const linkClipboard = useClipboard(createdInvite?.link ?? '');
  const messageClipboard = useClipboard(introMessage);

  const paidSlotsOpen = useMemo(() => {
    if (!plan?.seats) return false;
    if (plan.seats.maxSeats === null) return true;
    return plan.seats.paidMemberCount + plan.seats.pendingPaidInvitations < plan.seats.maxSeats;
  }, [plan?.seats]);

  // Seat copy, derived once so the JSX carries a single value per slot.
  const constrainedSeatHint = paidSlotsOpen
    ? `${plan?.seats?.paidMemberCount ?? 0}/${plan?.seats?.paidSeatCount ?? 1} paid seat used`
    : 'Your plan includes 1 paid seat. Upgrade to Team to add more.';
  const paidSeatHint = isTeam
    ? 'Stripe previews any prorated charge before the seat is added'
    : constrainedSeatHint;
  const paidSubmitLabel = paidSlotsOpen ? 'Create invite link' : 'Upgrade to invite paid member';
  const nonGuestSubmitLabel = isTeam ? 'Continue to seat purchase' : paidSubmitLabel;
  const submitLabel = mode === 'guest' ? 'Create invite link' : nonGuestSubmitLabel;

  const paidGate = useUpgradeGate('invite-member', {
    paidMemberCount: plan?.seats?.paidMemberCount ?? 0,
    paidSeatCount: plan?.seats?.paidSeatCount ?? 1,
  });

  // Reset whenever the modal is opened fresh.
  useEffect(() => {
    if (isOpen) {
      setMode('guest');
      setEmail('');
      setPaidRole('MEMBER');
      setPromotionCode('');
      setRedeemingPromotion(false);
      setBusy(false);
      setStage('compose');
      setPreview(null);
      setShowMessage(false);
      setCreatedInvite(null);
    }
  }, [isOpen]);

  // Auto-select the link the moment we land on the success stage so
  // Cmd+C / Ctrl+C just works without aiming at the input.
  useEffect(() => {
    if (stage === 'success' && linkInputRef.current) {
      linkInputRef.current.focus();
      selectFromStart(linkInputRef.current);
    }
  }, [stage]);

  function invalidateInvitations() {
    if (!workspace) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.invitations(workspace.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.plan(workspace.id) });
  }

  function buildLink(token: string): string {
    return `${window.location.origin}/auth?invitation=${token}`;
  }

  async function sendGuestInvite() {
    if (!workspace || !email) return;
    setBusy(true);
    try {
      const response = await inviteGuest(apiService, email);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Could not generate invitation');
      }
      safeAnalytics.track(ANALYTICS_EVENTS.INVITATION_CREATED, {
        workspace_id: workspace.id,
        intended_role: 'GUEST',
      } as never);
      // Distinct guest-funnel event so PostHog can chart guest invites
      // independently of paid seats (which fire SEAT_PURCHASED).
      safeAnalytics.track(ANALYTICS_EVENTS.GUEST_INVITED, {
        workspace_id: workspace.id,
      } as never);
      invalidateInvitations();
      setCreatedInvite({
        email,
        link: buildLink(response.data.invitationToken),
        role: 'guest',
      });
      setStage('success');
    } catch (err) {
      toast({
        title: 'Could not invite guest',
        description: err instanceof Error ? err.message : 'Please try again',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function startPaidFlow() {
    if (!workspace || !email) return;
    if (!isTeam) {
      if (paidSlotsOpen) {
        // There's an open paid slot — invite directly without Stripe.
        setBusy(true);
        try {
          const response = await inviteMemberDirect(apiService, email, paidRole);
          if (!response.success || !response.data) {
            throw new Error(response.error?.message ?? 'Could not create invitation');
          }
          safeAnalytics.track(ANALYTICS_EVENTS.INVITATION_CREATED, {
            workspace_id: workspace.id,
            intended_role: paidRole,
          } as never);
          invalidateInvitations();
          setCreatedInvite({
            email,
            link: buildLink(response.data.invitationToken),
            role: paidRole === 'ADMIN' ? 'admin' : 'member',
          });
          setStage('success');
        } catch (err) {
          toast({
            title: 'Could not send invitation',
            description: err instanceof Error ? err.message : 'Please try again',
            status: 'error',
          });
        } finally {
          setBusy(false);
        }
        return;
      }
      paidGate.promptUpgrade();
      onClose();
      return;
    }
    setBusy(true);
    try {
      const response = await previewAddSeat(apiService);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Could not preview seat charge');
      }
      // Defaults keep an older backend (one that predates the trial
      // fields) rendering exactly as it does today rather than crashing.
      setPreview({
        amountDueTodayCents: response.data.amountDueTodayCents ?? response.data.proratedAmountCents,
        recurringAmountCents: response.data.recurringAmountCents ?? null,
        currency: response.data.currency,
        requiresPaymentMethod: response.data.requiresPaymentMethod === true,
        paymentMethodReason: seatPaymentMethodReason(response.data.paymentMethodReason),
        isTrial: response.data.isTrial === true,
        trialEnd: response.data.trialEnd ?? null,
        trialSeatsRemaining: response.data.trialSeatsRemaining ?? null,
      });
      setStage('confirm-seat');
    } catch (err) {
      toast({
        title: 'Could not load seat preview',
        description: err instanceof Error ? err.message : 'Please try again',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function redeemPromotionCode() {
    if (!workspace || !promotionCode.trim()) return;
    setRedeemingPromotion(true);
    try {
      const response = await redeemPromotion(apiService, promotionCode);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Could not redeem promotion code');
      }
      safeAnalytics.track(ANALYTICS_EVENTS.BILLING_PROMOTION_REDEEMED, {
        workspace_id: workspace.id,
        fully_discounted: response.data.fullyDiscounted,
      } as never);
      setPromotionCode('');
      invalidateInvitations();
      toast({
        title: 'Promotion redeemed',
        description: 'Checking the updated seat total with Stripe now.',
        status: 'success',
      });
      await startPaidFlow();
    } catch (err) {
      toast({
        title: 'Could not redeem promotion',
        description: err instanceof Error ? err.message : 'Please try again',
        status: 'error',
      });
    } finally {
      setRedeemingPromotion(false);
    }
  }

  async function confirmPurchase() {
    if (!workspace || !email || !preview) return;
    setBusy(true);
    try {
      const response = await purchaseSeatAndInvite(apiService, email, paidRole);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Could not complete purchase');
      }
      safeAnalytics.track(ANALYTICS_EVENTS.INVITATION_CREATED, {
        workspace_id: workspace.id,
        intended_role: paidRole,
        prorated_amount_cents: response.data.proratedAmountCents,
        currency: response.data.currency,
      } as never);
      // Trial→paid conversion event. PostHog cohorts use this for
      // funnel analysis without having to filter INVITATION_CREATED
      // by paid-vs-guest role.
      safeAnalytics.track(ANALYTICS_EVENTS.SEAT_PURCHASED, {
        workspace_id: workspace.id,
        intended_role: paidRole,
        prorated_amount_cents: response.data.proratedAmountCents,
        currency: response.data.currency,
      } as never);
      invalidateInvitations();
      // What Stripe actually collected, not what the preview predicted it
      // would. The preview can be stale (a promo redeemed since, a card
      // attached in another tab), and "no charge today" over a real charge is
      // the claim this whole screen exists not to make. The trial is only
      // named as the REASON for a free seat when there is actually a trial: a
      // fully discounted seat on an active subscription is free too.
      const chargedCents = response.data.proratedAmountCents;
      const trialReason = preview.isTrial ? ' This seat is included in your trial.' : '';
      toast({
        title: 'Seat added',
        description:
          chargedCents === 0
            ? `No charge today.${trialReason}`
            : `Charged ${formatUsd(chargedCents, response.data.currency)} for the new seat.`,
        status: 'success',
      });
      setCreatedInvite({
        email,
        link: buildLink(response.data.invitationToken),
        role: paidRole === 'ADMIN' ? 'admin' : 'member',
      });
      setStage('success');
    } catch (err) {
      toast({
        title: 'Seat purchase failed',
        description: err instanceof Error ? err.message : 'Please try again',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  // Null whenever the backend didn't send a trial end (or sent something
  // unparseable), so every caller can fall back to date-free copy.
  const trialEndLabel = useMemo(() => {
    if (!preview?.trialEnd) return null;
    const parsed = new Date(preview.trialEnd);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
  }, [preview?.trialEnd]);

  // `maxGuests: null` from the policy resolver means UNLIMITED, and every tier
  // returns it since the 2026-09 pricing change. `?? 0` turned that into a cap
  // of zero, so the dialog reported "0/0 guest slots used", disabled the Guest
  // role and showed "All guest slots are used" on every workspace on every
  // plan. It also made the `guestCap === null` branches below unreachable,
  // which is why the unlimited wording never appeared to contradict it.
  //
  // A not-yet-loaded plan also lands here as null, and that is the right
  // failure direction: the dialog stays usable and the server decides. The
  // backend gate is authoritative and already skips a null cap
  // (`workspace-policy.service.ts`: `cap !== null && used >= cap`).
  const guestCap: number | null = plan?.seats?.maxGuests ?? null;
  const guestUsed = (plan?.seats?.guestCount ?? 0) + (plan?.seats?.pendingGuestInvitations ?? 0);
  const guestSlotsOpen = guestCap === null || guestUsed < guestCap;

  // Bigger modal on success so the link has room to breathe; compact
  // for the compose/preview steps which are vertically dense.
  const modalSize = stage === 'success' ? 'lg' : 'md';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {stage === 'confirm-seat' && 'Confirm seat purchase'}
          {stage === 'success' && 'Share the invite'}
          {stage === 'compose' && 'Invite to workspace'}
        </ModalHeader>
        <ModalCloseButton />

        {stage === 'compose' && (
          <>
            <ModalBody pb={2}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs" textTransform="uppercase" color="text.muted">
                    Recipient email
                  </FormLabel>
                  <Input
                    type="email"
                    placeholder="teammate@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.trim())}
                    autoComplete="off"
                  />
                  <FormHelperText fontSize="xs" color="text.muted">
                    They must sign in or sign up with this exact address to join. Bower
                    doesn&rsquo;t email them; you&rsquo;ll get a link to share on the next screen.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" textTransform="uppercase" color="text.muted">
                    Role
                  </FormLabel>
                  <RadioGroup value={mode} onChange={(v) => setMode(v as InviteMode)}>
                    <VStack spacing={2} align="stretch">
                      <Radio value="guest" isDisabled={!guestSlotsOpen}>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm">
                            Guest{' '}
                            <Text as="span" fontSize="xs" color="text.muted">
                              : view-only, free
                            </Text>
                          </Text>
                          <Text fontSize="xs" color="text.muted">
                            {guestCap === null
                              ? `${guestUsed} guests on this workspace`
                              : `${guestUsed}/${guestCap} guest slots used`}
                          </Text>
                        </VStack>
                      </Radio>
                      <Radio value="paid">
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm">
                            Paid member{' '}
                            <Text as="span" fontSize="xs" color="text.muted">
                              : full edit access
                            </Text>
                          </Text>
                          <Text fontSize="xs" color="text.muted">
                            {paidSeatHint}
                          </Text>
                        </VStack>
                      </Radio>
                    </VStack>
                  </RadioGroup>
                </FormControl>

                {mode === 'paid' && canGrantAdmin && (
                  <FormControl>
                    <FormLabel fontSize="xs" textTransform="uppercase" color="text.muted">
                      Workspace role for the new member
                    </FormLabel>
                    <RadioGroup
                      value={paidRole}
                      onChange={(v) => setPaidRole(v as 'ADMIN' | 'MEMBER')}
                    >
                      <HStack spacing={5}>
                        <Radio value="MEMBER">
                          <Text fontSize="sm">Member</Text>
                        </Radio>
                        <Radio value="ADMIN">
                          <Text fontSize="sm">Admin</Text>
                        </Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>
                )}

                {mode === 'guest' && !guestSlotsOpen && (
                  <StatusBanner
                    tone="warning"
                    title="All guest slots are used"
                    description={
                      isTeam
                        ? 'Add a paid seat to free up a guest slot per paid seat.'
                        : 'Upgrade your plan to invite more guests.'
                    }
                  />
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                isDisabled={
                  !email ||
                  (mode === 'guest' && !guestSlotsOpen) ||
                  (mode === 'paid' && !isTeam && !paidSlotsOpen)
                }
                isLoading={busy}
                onClick={() => (mode === 'guest' ? sendGuestInvite() : startPaidFlow())}
              >
                {submitLabel}
              </Button>
            </ModalFooter>
          </>
        )}

        {stage === 'confirm-seat' && preview && preview.requiresPaymentMethod && (
          <>
            <ModalBody pb={2}>
              <VStack spacing={4} align="stretch">
                <StatusBanner
                  tone="warning"
                  title="Add a payment method before adding seats"
                  // Only `cap-reached` may name the cap. A null reason (an
                  // older backend, or a value this client does not know) gets
                  // the generic ask, because claiming a spent cap without the
                  // backend saying so would be a lie, as it was for a 5-seat
                  // workspace until 2026-08-11.
                  description={
                    preview.paymentMethodReason === 'cap-reached'
                      ? `Your trial includes ${TRIAL_CARDLESS_SEAT_CAP} card-free seats and you have used them all. Add a payment method, or redeem an eligible code and check the preview again.`
                      : 'Adding a seat to this workspace needs a payment method. Add one, or redeem an eligible code and check the preview again.'
                  }
                />
                <Text fontSize="sm" color="text.secondary">
                  If you add a payment method, Stripe will send you back to the Members tab. You can
                  also redeem a promotion code below; Bower will ask Stripe for a fresh seat preview
                  before it can add the seat.
                </Text>
                <FormControl>
                  <FormLabel fontSize="xs" textTransform="uppercase" color="text.muted">
                    Promotion code
                  </FormLabel>
                  <HStack align="end">
                    <Input
                      aria-label="Promotion code"
                      autoComplete="off"
                      value={promotionCode}
                      onChange={(event) => setPromotionCode(event.target.value)}
                    />
                    <Button
                      variant="outline"
                      isLoading={redeemingPromotion}
                      isDisabled={!promotionCode.trim() || redeemingPromotion}
                      onClick={redeemPromotionCode}
                    >
                      Redeem code
                    </Button>
                  </HStack>
                  <FormHelperText fontSize="xs" color="text.muted">
                    If Stripe confirms the added seat costs $0 after redemption, no payment method
                    is needed.
                  </FormHelperText>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => setStage('compose')} mr={2}>
                Back
              </Button>
              <Button
                isLoading={portalMutation.isPending}
                onClick={() =>
                  portalMutation.mutate({
                    intent: 'payment-method-update',
                    flowType: 'payment-method-update',
                  })
                }
              >
                Add payment method
              </Button>
            </ModalFooter>
          </>
        )}

        {stage === 'confirm-seat' && preview && !preview.requiresPaymentMethod && (
          <SeatConfirmationStage
            preview={preview}
            email={email}
            paidRole={paidRole}
            trialEndLabel={trialEndLabel}
            onBack={() => setStage('compose')}
            onConfirm={confirmPurchase}
            busy={busy}
          />
        )}

        {stage === 'success' && createdInvite && (
          <>
            <ModalBody pb={2}>
              <VStack spacing={5} align="stretch">
                {/* Who the invite is for, in one line: the success mark, the
                    address the link is bound to, and the role it grants. This
                    used to be a bordered grey card with its own "Invite ready
                    for" caption and a floating uppercase role, which spent a
                    lot of vertical space and visual weight on three facts the
                    admin already knows. */}
                <HStack spacing={2.5} align="center">
                  <Box
                    bg="success.500"
                    color="white"
                    borderRadius="full"
                    boxSize={5}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <MaterialIcon name="check" color="inherit" boxSize={3.5} />
                  </Box>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="text.primary"
                    noOfLines={1}
                    flex="1"
                    minW={0}
                  >
                    {/* The check glyph is decorative (aria-hidden in the
                        MaterialIcon wrapper), and dropping the old "Invite
                        ready for" caption took the only text that said the
                        invitation now exists. Sighted users read that off the
                        header; this puts it back for everyone else without
                        reintroducing the visual line. */}
                    {/* Trailing space inside the string literal, not JSX
                        whitespace, which JSX would strip. The clipped node
                        abuts the email in textContent, so without it the
                        row reads "Invitation created forname@example.com". */}
                    <VisuallyHidden>{'Invitation created for '}</VisuallyHidden>
                    {createdInvite.email}
                  </Text>
                  {/* `size="md"` with an `xs` label, which is not the same as
                      `size="sm"`. The sm pill is 20px tall and this typeface's
                      ink box for the label measures 21px, so the glyphs
                      overflowed the pill and read as sitting high next to the
                      email. The md pill is 24px, which leaves real room around
                      a 12px label; md's own 14px label would match the email
                      and make a secondary tag shout. */}
                  {/* The shared Badge, not a Chakra `Tag`. There is no `Tag`
                      entry in theme/components.ts, so a Tag here rendered
                      Chakra's stock cool-grey pill — the one tint the neutral
                      Badge variant exists to avoid — a screen away from the
                      members list that uses the real thing. `role` here is the
                      LOWER-case union ('admin'), not the Prisma enum, so it
                      needs the shared label helper rather than the CSS
                      `capitalize` it used to carry — same helper the members
                      list uses, so the two screens cannot drift apart. */}
                  <Badge flexShrink={0}>{getRoleLabel(createdInvite.role)}</Badge>
                </HStack>

                {/* The hero. Large, monospace, full-width, auto-selected on
                    entry, with the only primary button on the screen beside
                    it. One sentence under it disarms the two questions the
                    screen always gets: did Bower send this, and can it be
                    forwarded. */}
                <Box>
                  <Text textStyle="eyebrow" color="text.muted" mb={2}>
                    Invitation link
                  </Text>
                  <ContainerStack threshold={420} gap={2} align="stretch">
                    <Input
                      ref={linkInputRef}
                      value={createdInvite.link}
                      isReadOnly
                      fontFamily="mono"
                      fontSize="sm"
                      bg="bg.surface"
                      onFocus={(e) => selectFromStart(e.currentTarget)}
                      aria-label="Invitation link"
                      flex="1"
                    />
                    <Button
                      onClick={() => {
                        linkClipboard.onCopy();
                        toast({ title: 'Link copied to clipboard', status: 'success' });
                      }}
                      leftIcon={
                        <MaterialIcon
                          name={linkClipboard.hasCopied ? 'check' : 'content_copy'}
                          color="inherit"
                        />
                      }
                      sx={{ '@container (min-width: 420px)': { minWidth: '130px' } }}
                    >
                      {linkClipboard.hasCopied ? 'Copied' : 'Copy link'}
                    </Button>
                  </ContainerStack>
                  <Text fontSize="xs" color="text.muted" mt={2}>
                    Bower doesn&rsquo;t email this for you. Send it however you&rsquo;d normally
                    reach them; only that address can use it to join.
                  </Text>
                </Box>

                {/* Secondary affordance: the templated intro, so nobody has to
                    decide what to write. Ghost buttons and a closed preview
                    keep it clearly subordinate to the link above. */}
                <Box>
                  <HStack spacing={1}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        messageClipboard.onCopy();
                        toast({ title: 'Message copied to clipboard', status: 'success' });
                      }}
                      leftIcon={
                        <MaterialIcon
                          name={messageClipboard.hasCopied ? 'check' : 'content_copy'}
                          color="inherit"
                        />
                      }
                    >
                      {messageClipboard.hasCopied ? 'Message copied' : 'Copy intro message'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="text.muted"
                      onClick={() => setShowMessage((open) => !open)}
                      aria-expanded={showMessage}
                      // Off while the textarea is unmounted: a dangling IDREF
                      // fails an a11y audit and is worse than no attribute at
                      // all. Same gate as SettingsSearchBox's listbox.
                      aria-controls={showMessage ? introMessageId : undefined}
                      rightIcon={
                        <MaterialIcon
                          name={showMessage ? 'expand_less' : 'expand_more'}
                          color="inherit"
                        />
                      }
                    >
                      {showMessage ? 'Hide' : 'Preview'}
                    </Button>
                  </HStack>
                  {showMessage && (
                    <Textarea
                      id={introMessageId}
                      value={introMessage}
                      isReadOnly
                      fontSize="sm"
                      // Tall enough for the whole template at modal width. A
                      // preview the reader has to scroll defeats the point of
                      // asking to see it.
                      rows={8}
                      mt={2}
                      bg="bg.surface"
                      fontFamily="body"
                      onFocus={(e) => selectFromStart(e.currentTarget)}
                      aria-label="Suggested intro message"
                    />
                  )}
                </Box>
              </VStack>
            </ModalBody>
            {/* No dismiss button. The modal convention is that the
                top-right X closes it and the footer carries only primary
                actions, and there is no primary action left here once the
                link is copied. "Done" was a Cancel button under another
                word: the no-Cancel-in-a-footer lint rule matches the
                literal string, so the synonym walked straight past it.
                That rule is now backed by a structural guard:
                scripts/check-modal-close-convention.mjs. */}
            <ModalFooter justifyContent="flex-start">
              <Text fontSize="xs" color="text.muted">
                Find it again under Members &rarr; Pending invitations.
              </Text>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
